import { pick } from 'shared/utils/utils';
import { foldIndexesToSpans } from 'shared/excel/span';
import { RangeProxy } from 'src/services/runtime/rangeProxy';


export class DataTable {
  autoRangesRecalculation = true;
  savedFilter = [];
  hiddenColumnSpans = [];

  constructor(worksheet) {
    this.worksheet = worksheet.load(RangeProxy.PropsToLoad.Worksheet);
    this.context = worksheet.context;
  }

  get range() { return this._rangeProxy.impl; }
  get dataBodyRange() { return this._dataBodyRangeProxy.impl; }
  get headerRowRange() { return this._headerRowRangeProxy?.impl; }
  get totalRowRange() { return this._totalRowRangeProxy?.impl; }

  get rowIndex() { return this._rangeProxy.rowIndex; }
  get columnIndex() { return this._rangeProxy.columnIndex; }
  get rowCount() { return this._rangeProxy.rowCount; }
  get columnCount() { return this._rangeProxy.columnCount; }

  get realDataRowCount() { return this.isEmpty ? 0 : this.dataRowCount; }
  get dataRowIndex() { return this._dataBodyRangeProxy.rowIndex; }
  get dataColumnIndex() { return this._dataBodyRangeProxy.columnIndex; }
  get dataRowCount() { return this._dataBodyRangeProxy.rowCount; }
  get dataColumnCount() { return this._dataBodyRangeProxy.columnCount; }

  get realTable() { return this.table; }
  get isRealTable() { return !!this.table; }
  get hasTotals() { return !!this.totalRowRange; }
  get hasHeaders() { return !!this.headerRowRange; }

  static async fromRange(range, hasHeaders = true, hasTotals = false) {
    const t = new DataTable(range.worksheet);
    await t._syncProxiesWithRange(range, { hasHeaders, hasTotals });
    return t;
  }

  static async fromTable(table) {
    const t = new DataTable(table.worksheet);
    t.table = table;
    await t.syncProxiesWithTable();
    return t;
  }

  enableAutomaticRangesRecalculation(doThat) {
    this.autoRangesRecalculation = doThat;
  }

  toRelativeIndexes(indexes = {}) {
    return this._dataBodyRangeProxy.rangeAddress.toRelativeIndexes(indexes);
  }

  toAbsoluteIndexes(indexes = {}) {
    return this._dataBodyRangeProxy.rangeAddress.toAbsoluteIndexes(indexes);
  }

  async getVisibleRowSpans(range) {
    return await getVisibleRowSpans(range, this.dataRowIndex);
  }

  async getVisibleRowIndexes(range) {
    return await getVisibleRowIndexes(range, this.dataRowIndex);
  }

  getRows(index, count) {
    return this._dataBodyRangeProxy.getRows(index, count).impl;
  }

  getColumns(index, count) {
    return this._rangeProxy.getColumns(index, count).impl;
  }

  async getHeaders() {
    if (!this.hasHeaders) { return []; }

    const h = this.headerRowRange.load(['values']);
    await this.sync();
    return h.values[0].map(x => x.toString());
  }

  setHeaders(headers, columnOffset = 0) {
    if (this.hasHeaders) {
      this._headerRowRangeProxy
        .getColumns(columnOffset, headers.length)
        .impl.values = [headers];
    }
  }

  async safeSetValues(rowOffset, values, columnOffset = 0) {
    // Prepare empty ranges for values which then will be overwritten in 
    // this.setValues call. See this.insertRows for explanation
    if (this._tryToInsertEmptyRowsInCaseOfTable(rowOffset, values.length)) {
      await this.sync();
    }
    this.setValues(rowOffset, values, columnOffset);
  }

  // !!! In case of the table when appends to the end 
  // happens (rowsToAddCount > 0 && this.isRealTable)
  // it significantly slows down Excel for Mac and Win
  // So, if you know that you insert might involve adding hundreds or thousands of rows
  // use this.safeSetValues
  setValues(rowOffset, countOrValues, columnOffset = 0) {
    const isArray = Array.isArray(countOrValues);
    const rowCount = isArray ? countOrValues.length : countOrValues;
    const columnCount = isArray ? +countOrValues[0]?.length : this.columnCount - columnOffset;
    const values = isArray ? countOrValues : Array(rowCount).fill(Array(columnCount).fill(null));

    const rowsToPatchCount = Math.min(rowCount, this.realDataRowCount - rowOffset);
    const rowsToAddCount = rowCount - rowsToPatchCount;

    if (columnOffset + columnCount > this.columnCount) {
      throw new Error(`DataTable::setValues: invalid columns count. Expected: <= ${this.columnCount}, observed: ${columnCount}, offset: ${columnOffset}`);
    }

    if (rowsToPatchCount > 0) {
      const valuesToPatch = rowsToPatchCount === rowCount ? values : values.splice(0, rowsToPatchCount);
      this._dataBodyRangeProxy
        .getRows(rowOffset, rowsToPatchCount)
        .getColumns(columnOffset, columnCount)
        .impl.values = valuesToPatch;
    }

    if (rowsToAddCount > 0) {
      this.addRows(values, columnOffset);
    }
  }

  addRows(countOrValues, columnOffset = 0) {
    return this.insertRows(this.realDataRowCount, countOrValues, columnOffset);
  }

  addColumns(countOrValues, names = []) {
    return this.insertColumns(this.columnCount, countOrValues, names);
  }

  insertRows(index, countOrValues, columnOffset = 0) {
    const isArray = Array.isArray(countOrValues);
    const rowCount = isArray ? countOrValues.length : countOrValues;
    const columnCount = isArray ? countOrValues[0].length : this.columnCount - columnOffset;
    const values = isArray ? countOrValues : Array(rowCount).fill(Array(columnCount).fill(null));

    if (this.isRealTable) {
      if (this.columnCount === columnCount) {
        // !!! This call is VERY slow if we use real values (Microsoft issue)
        // But it works fine if all the values are null
        // That's why this._insertEmptyRanges exists: it calls this.addRows method with 
        // values that are all null and then once again this.setValue, where actual values
        // are written to the ranges allocated in previous step
        this.table.rows.add(index, values);
      }
      else {
        const toFill = Array(rowCount).fill(Array(this.columnCount).fill(null));
        this.table.rows.add(index, toFill);

        // It makes sence to set real values if 'countOrValues' was an array
        if (isArray) {
          this._dataBodyRangeProxy
            .getRows(index, rowCount)
            .getColumns(columnOffset, columnCount)
            .impl.values = values;
        }
      }
    }
    else {
      const newRangeProxy = this._dataBodyRangeProxy.getRows(index, rowCount);
      newRangeProxy.impl.insert(Excel.InsertShiftDirection.down);
      if (values) {
        newRangeProxy
          .getColumns(columnOffset, columnCount)
          .impl.values = values;
      }
    }

    this._resizeIfRecalculationEnabled(rowCount, 0);
    return this.getRows(index, rowCount);
  }

  insertColumns(index, countOrValues, names = []) {
    // console.log('DataTable::insertColumns: (isTable): ', this.isRealTable, index, countOrValues, names);

    const isArray = Array.isArray(countOrValues);
    const count = isArray ? countOrValues.length : countOrValues;

    if (this.table) {
      for (let i = 0; i < count; ++i) {
        const name = names.length > i ? names[i] : undefined;
        const values = isArray ? countOrValues.map(row => [row[i]]) : undefined;
        this.table.columns.add(index + i, values, name);
      }
    }
    else {
      const range = this._rangeProxy.getColumns(index, count).impl;
      const blankRange = range.insert(Excel.InsertShiftDirection.right);
      if (isArray) {
        blankRange.values = countOrValues;
      }
      if (this.hasHeaders && names.length) {
        const headerRange = this._headerRowRangeProxy.getColumns(index, count).impl;
        headerRange.values = [names];
      }
    }

    this._resizeIfRecalculationEnabled(0, count);
    return this.getColumns(index, count);
  }

  deleteRows(index, count) {
    // console.log('DataTable::deleteRows: (isTable): ', this.isRealTable, index, count);

    if (this.table) {
      this.table.rows.deleteRowsAt(index, count);
    }
    else {
      this._dataBodyRangeProxy
        .getRows(index, count).impl
        .delete(Excel.DeleteShiftDirection.up);
    }

    this._resizeIfRecalculationEnabled(-count, 0);
  }

  deleteColumns(index, count) {
    // console.log('DataTable::deleteColumns: (isTable): ', this.isRealTable, index, count);

    if (this.table) {
      for (let i = index + count - 1; i >= index; --i) {
        this.table.columns.getItemAt(i).delete();
      }
    }
    else {
      this._rangeProxy
        .getColumns(index, count).impl
        .delete(Excel.DeleteShiftDirection.left);
    }

    this._resizeIfRecalculationEnabled(0, -count);
  }

  clear() {
    if (this.realDataRowCount) {
      this.insertRows(0, 1);
      this.deleteRows(1, this.realDataRowCount - 1);
      this.isEmpty = true;
    }
  }

  fitRowsToSize(size) {
    const rowsDelta = size - this.realDataRowCount;
    if (rowsDelta) {
      rowsDelta < 0
        ? this.deleteRows(size, -rowsDelta)
        : this.insertRows(this.realDataRowCount, rowsDelta);
    }
  }

  fitColumnsToSize(size) {
    const columnsDelta = size - this.columnCount;
    if (columnsDelta) {
      columnsDelta < 0
        ? this.deleteColumns(size, -columnsDelta)
        : this.insertColumns(this.range.columnCount, columnsDelta);
    }
  }

  async convertToRealTable() {
    if (!this.isRealTable) {
      this.table = this.context.workbook.tables.add(this._rangeProxy.address, this.hasHeaders);
      const r = this.table.getRange();
      r.format.autofitColumns();
      r.format.autofitRows();
      await this.syncProxiesWithTable();
    }
  }

  _tryToInsertEmptyRowsInCaseOfTable(rowOffset, countOrValues, columnOffset = 0) {
    const isArray = Array.isArray(countOrValues);
    const rowCount = isArray ? countOrValues.length : countOrValues;

    const rowsToPatchCount = Math.min(rowCount, this.realDataRowCount - rowOffset);
    const rowsToAddCount = rowCount - rowsToPatchCount;

    // !!! this condition heavily relies on this.addRows implementation
    // See this.addRows for explanation
    if (rowsToAddCount > 0 && this.isRealTable) {
      this.addRows(countOrValues, columnOffset);
      return true;
    }

    return false;
  }

  _resizeIfRecalculationEnabled(rowDelta, columnDelta) {
    // console.log('DataTable::_resize: ', rowDelta, columnDelta, this.autoRangesRecalculation);
    if (!this.autoRangesRecalculation) {
      return;
    }

    const dataRowDiff = this.realDataRowCount + rowDelta;
    const isEmpty = dataRowDiff < 1;
    const dataRowCount = isEmpty ? 1 : dataRowDiff;
    const rowCount = dataRowCount + (+this.hasHeaders + +this.hasTotals);
    const columnCount = Math.max(this.columnCount + columnDelta, 1);
    const rangeProxy = this._rangeProxy.getOffsetAndResizedRange({ rowDelta: rowCount, columnDelta: columnCount }, { useAbsoluteDelta: true });
    this._recalculateRanges(rangeProxy, { isEmpty });
  }

  _recalculateRanges(newRangeProxy, params = {}) {
    const { isEmpty = false, hasHeaders = this.hasHeaders, hasTotals = this.hasTotals } = params;

    this._rangeProxy = newRangeProxy;

    // We must that we have at least one row in this range
    this._dataBodyRangeProxy = newRangeProxy.getOffsetAndResizedRange({
      rowOffset: +hasHeaders,
      rowDelta: newRangeProxy.rowCount - (+hasHeaders + +hasTotals) || 1,
    }, { useAbsoluteDelta: true });
    this._headerRowRangeProxy = hasHeaders ? newRangeProxy.getRow(0) : null;
    this._totalRowRangeProxy = hasTotals ? newRangeProxy.getLastRow() : null;

    this.isEmpty = isEmpty;
  }

  getOffsetAndResizedRange(deltas = {}, options = {}) {
    return this._rangeProxy.getOffsetAndResizedRange(deltas, options).impl;
  }

  async getOffsetAndResizedDataTable(deltas, options) {
    const r = this.getOffsetAndResizedRange(deltas, options);
    return await DataTable.fromRange(r, this.hasHeaders, this.hasTotals);
  }

  async pushAndUnhideColumns() {
    this.hiddenColumnSpans = await getHiddenColumnSpans(this.range.getRow(0));
    console.log('DataTable::pushAndUnhideColumns: Hidden columns: ', this.hiddenColumnSpans);
    await this._hideColumns(false);
  }

  async popAndHideColumns() {
    await this._hideColumns(true);
    this.hiddenColumnSpans = [];
  }

  async _hideColumns(doHide) {
    for (const { index, count } of this.hiddenColumnSpans) {
      this.worksheet.getRangeByIndexes(
        this.range.rowIndex, index,
        1, count)
        .columnHidden = doHide;
    }
    await this.sync();
  }

  async syncProxiesWithTable() {
    this.table.load(['showTotals', 'showHeaders']);
    const range = this.table.getRange().load(RangeProxy.PropsToLoad.Range);
    await this.sync();
    const { showTotals: hasTotals, showHeaders: hasHeaders } = this.table;
    await this._syncProxiesWithRange(range, { areRangePropsLoaded: true, hasTotals, hasHeaders });
  }

  async _syncProxiesWithRange(range, params = {}) {
    const { areRangePropsLoaded = false } = params;
    if (!areRangePropsLoaded) {
      range.load(RangeProxy.PropsToLoad.Range);
      await this.sync();
    }

    this._recalculateRanges(RangeProxy.wrap(range), params);
    await this.fetchRangesProps();
  }

  async fetchRangesProps() {
    [this.range, this.dataBodyRange, this.headerRowRange, this.totalRowRange].forEach(r => {
      if (r) {
        r.load(RangeProxy.PropsToLoad.Range);
        r.worksheet.load(RangeProxy.PropsToLoad.Worksheet);
      }
    });

    if (this.dataRowCount === 1) {
      this.dataBodyRange.load(['values']);
    }
    else {
      this.isEmpty = false;
    }

    await this.sync();

    if (this.dataRowCount === 1) {
      this.isEmpty = this.dataBodyRange.values[0].every(x => !x);
    }
  }

  async sync() {
    await this.context.sync();
  }
}


//
// --- UTILS ---
//
async function indexesGen(range, basis = 0, isRow, isVisible) {
  range.load(['rowIndex', 'rowCount', 'columnIndex', 'columnCount']);
  const source = isRow ? range.getColumn(0) : range.getRow(0);
  const cells = source.getCellProperties({ hidden: true });

  await range.context.sync();

  const { columnCount, rowCount, rowIndex, columnIndex } = range;
  // If 0 === basis, then count from the beginning of sheet, otherwise take basis in account
  const basisIndex = (isRow ? rowIndex : columnIndex) - basis;
  const count = isRow ? rowCount : columnCount;

  function* gen() {
    for (let i = 0; i < count; ++i) {
      const cell = isRow ? cells.value[i][0] : cells.value[0][i];
      if (cell.hidden != isVisible) { // Emulating xor
        yield i + basisIndex;
      }
    }
  }

  return gen();
}

async function getSpans(range, basis, isRow, isVisible) {
  return foldIndexesToSpans(await indexesGen(range, basis, isRow, isVisible));
}

async function getIndexes(range, basis, isRow, isVisible) {
  const out = [];
  for (const x of await indexesGen(range, basis, isRow, isVisible)) {
    out.push(x);
  }
  return out;
}

async function getVisibleRowIndexes(range, basis = 0) {
  return await getIndexes(range, basis, true, true);
}

async function getVisibleRowSpans(range, basis = 0) {
  return await getSpans(range, basis, true, true);
}

async function getHiddenColumnSpans(range, basis = 0) {
  return await getSpans(range, basis, false, false);
}

export async function clearAllFilters(worksheet) {
  const { context } = worksheet;

  worksheet.load(['autoFilter']);
  worksheet.tables.load(['items']);

  await context.sync();

  const tables = worksheet.tables.items;
  if (tables.length) {
    tables.forEach(x => x.load(['autoFilter']));
    await context.sync();
  }

  const filtersSources = [worksheet, ...tables]
    .map(x => ({
      autoFilter: x.autoFilter.load(['criteria', 'enabled']),
      range: x.autoFilter.getRangeOrNullObject()
    }));
  await context.sync();

  const activeSources = filtersSources
    .reduce((out, { autoFilter, range }) => {
      if (!autoFilter.enabled || range.isNullObject) {
        return out;
      }

      const filters = autoFilter.criteria
        .reduce((activeFilters, x, i) => {
          if (x.criterion1 || x.criterion2 || x.values) {
            activeFilters.push({ columnIndex: i, filter: pick(x, ['criterion1', 'criterion2', 'filterOn', 'operator', 'values']) });
          }
          return activeFilters;
        }, []);

      if (filters.length) {
        out.push({ autoFilter, filters });
        autoFilter.clearCriteria();
      }

      return out;
    }, []);

  await context.sync();
  return activeSources;
}

export async function applyFilters(sources) {
  if (sources.length) {
    sources.forEach(({ autoFilter, filters }) =>
      filters.forEach(x => autoFilter.apply(autoFilter.getRange(), x.columnIndex, x.filter)));
    await sources[0].autoFilter.context.sync();
  }
}
